import { useState, useEffect } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, EyeOff } from "lucide-react";
import AdminSidebar from "@/components/layout/AdminSidebar";
import AdminPageHeader from "@/components/layout/AdminPageHeader";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Button } from "@/components/ui/button";

const Admin = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Use the central entitlement hook so the "View as User" toggle is honored.
  const { isAdmin, isRealAdmin, isImpersonating } = useEntitlement();
  const { setViewAsUser } = useImpersonation();
  const location = useLocation();
  const navigate = useNavigate();
  const currentSection = location.pathname.split('/')[2] || 'overview';

  const sectionMeta: Record<string, { title: string; subtitle: string }> = {
    overview: { title: "Admin", subtitle: "Dashboard Overview" },
    topics: { title: "Admin", subtitle: "Topics" },
    notes: { title: "Admin", subtitle: "Notes" },
    mcqs: { title: "Admin", subtitle: "Question Bank" },
    labels: { title: "Admin", subtitle: "Bulk Labels" },
    import: { title: "Admin", subtitle: "Import" },
    ai: { title: "Admin", subtitle: "AI Tools" },
    linking: { title: "Admin", subtitle: "Link MCQs/Notes" },
    users: { title: "Admin", subtitle: "User Management" },
    billing: { title: "Admin", subtitle: "Subscription & Billing" },
    "exam-labels": { title: "Admin", subtitle: "Exams & Pricing" },
    audit: { title: "Admin", subtitle: "Audit Logs" },
    feedback: { title: "Admin", subtitle: "Feedback & Support" },
    analytics: { title: "Admin", subtitle: "Analytics & Reports" },
    settings: { title: "Admin", subtitle: "Settings" },
    review: { title: "Admin", subtitle: "Review Queue" },
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Quick exit: press "Esc" outside inputs to return to user app
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      navigate("/dashboard");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // Silently redirect non-admins away from /admin/* and log the attempt.
  // Real admins currently impersonating a user keep the friendly "Exit User View" screen below.
  useEffect(() => {
    if (loading) return;
    if (!session) return; // ProtectedRoute handles redirect to /auth
    if (isAdmin) return;
    if (isRealAdmin && isImpersonating) return; // intentional, not unauthorized

    // Fire-and-forget audit log; never block the redirect on failure.
    (async () => {
      try {
        await supabase.from("admin_audit_logs").insert({
          user_id: session.user.id,
          action: "unauthorized_admin_access_attempt",
          entity_type: "admin_route",
          new_values: {
            path: location.pathname,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          },
        });
      } catch {
        /* swallow — auditing must never trap the user on /admin */
      }
    })();

    navigate("/dashboard", { replace: true });
  }, [loading, session, isAdmin, isRealAdmin, isImpersonating, location.pathname, navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session || !isAdmin) {
    const blockedByImpersonation = !!session && isRealAdmin && isImpersonating;
    if (blockedByImpersonation) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center max-w-md space-y-3">
            <h1 className="text-2xl font-bold mb-2">Admin Panel</h1>
            <p className="text-muted-foreground">
              You're previewing the app as a regular user. Exit user view to access the Admin Panel.
            </p>
            <Button onClick={() => setViewAsUser(false)} className="mt-2">
              <EyeOff className="mr-2 h-4 w-4" /> Exit User View
            </Button>
          </div>
        </div>
      );
    }
    // Non-admin: redirect effect above is taking the user to /dashboard.
    return null;
  }

  const meta = sectionMeta[currentSection] || sectionMeta.overview;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        
        <main className="flex-1 flex flex-col">
          <AdminPageHeader
            title={meta.title}
            section={meta.subtitle}
            userEmail={session?.user?.email}
            onSignOut={handleSignOut}
          />
          
          <div className="flex-1 overflow-y-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Admin;
